import { normalizeEmptyResponseToSuccessResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/infrastructure';
import {
  SuccessCallback,
  ErrorCallback,
} from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { awaitDataServiceFetcher } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/DataServiceFetchers';
import { ITransferRepository } from '../entities/ITransferRepository';
import {
  DeleteDocument,
  ExchangeCalculate,
  ExchangeRates,
  Forecast,
  InitiateTransferRequest,
  PostDocument,
  Routing,
  Document,
  SwiftCode,
  SwiftCity,
  SwiftBank,
  BopCode,
  VocCode,
  CurrencySearch,
  Vop,
} from '../entities';
import { Priorities, ValueDateResponseBody } from '../entities/ValueDateResponseDTO';
import { InitiateTransferResponseBody } from '../entities/InitiateTransferResponseDTO';
import { MakeTransferResponseBody } from '../entities/MakeTransferResponseDTO';
import { RoutingResponseBody } from '../entities/RoutingResponseDTO';
import { ExchangeRatesResponseBody } from '../entities/ExchangeRatesResponseDTO';
import { ForecastResponseBody } from '../entities/ForecastResponseDTO';
import { ExchangeCalculateResponseBody } from '../entities/ExchangeCalculateResponseDTO';
import { PostDocumentResponseBody } from '../entities/PostDocumentResponseDTO';
import { BopCodesIdentifiers } from '../entities/BopCode';
import { DealerRateExecute } from '../entities/DealerRateExecute';
import { DealerRateExecuteResponseBody } from '../entities/DealerRateExecuteResponseDTO';
import { DealerRatesWithCalculatedTimer } from '../entities/DealerRatesResponseDTO';
import { CurrencySearchResponseBody } from '../entities/CurrencySearchResponseDTO';
import { SwiftCountry } from '../entities/SwiftCountry';
import { OrderChargesResponseBody } from '../entities/OrderChargesResponseDTO';
import { ForecastRoutingResponseBody } from '../entities/ForecastRoutingResponseDTO';
import { VopResponseBody } from '../entities/VopResponseDTO';
import {
  initiateTransferDTOToInitiateTransferAdapter,
  makeTransferDTOToMakeTransferAdapter,
  routingDTOToRoutingAdapter,
  forecastDTOToForecastAdapter,
  exchangeCalculateDTOToExchangeCalculateAdapter,
  exchangeRatesDTOToExchangeRatesAdapter,
  documentsResponseDTOToDocumentsAdapter,
  postDocumentResponseDTOToPostDocumentAdapter,
  swiftCodesResponseDTOToSwiftCodesAdapter,
  swiftCitiesResponseDTOToSwiftCitiesResponseAdapter,
  swiftBanksResponseDTOToSwiftBanksAdapter,
  bopCodesResponseDTOToBopCodesAdapter,
  vocCodesResponseDTOToVocCodesAdapter,
  dealerRateExecuteDTOToDealerRateExecuteAdapter,
  dealerRatesResponseDTOToDealerRatesAdapter,
  bicSwiftCodesResponseDTOToSwiftCodesAdapter,
  valueDateResponseDTOToValueDateResponseAdapter,
  swiftCountriesResponseDTOToSwiftCountriesResponseAdapter,
  currencySearchResponseDTOToCurrencySearchResponseAdapter,
  orderChargesResponseDTOToorderChargesResponseAdapter,
  forecastRoutingDTOToForecastRoutingAdapter,
  vopDTOToVopAdapter,
} from './';

class TransferRepositoryImpl implements ITransferRepository {
  constructor() {
    this.Initiate = this.Initiate.bind(this);
    this.MakeTransfer = this.MakeTransfer.bind(this);
    this.Routing = this.Routing.bind(this);
    this.ExchangeCalculate = this.ExchangeCalculate.bind(this);
    this.ExchangeRates = this.ExchangeRates.bind(this);
    this.PostDocument = this.PostDocument.bind(this);
    this.GetDocuments = this.GetDocuments.bind(this);
    this.GetBankSwiftCodes = this.GetBankSwiftCodes.bind(this);
    this.GetSwiftCities = this.GetSwiftCities.bind(this);
    this.GetSwiftBanks = this.GetSwiftBanks.bind(this);
    this.GetBopCodes = this.GetBopCodes.bind(this);
    this.GetVocCodes = this.GetVocCodes.bind(this);
    this.DealerRateExecute = this.DealerRateExecute.bind(this);
    this.GetDealerRates = this.GetDealerRates.bind(this);
    this.InitiateRequestDealerTransfer = this.InitiateRequestDealerTransfer.bind(this);
    this.GetSwiftCodesByBic = this.GetSwiftCodesByBic.bind(this);
    this.RequestNewDealerRate = this.RequestNewDealerRate.bind(this);
    this.RejectDealerRate = this.RejectDealerRate.bind(this);
    this.InitiateCurrentDealerRate = this.InitiateCurrentDealerRate.bind(this);
    this.ExecuteWithCurrentDealerRate = this.ExecuteWithCurrentDealerRate.bind(this);
    this.GetTransferValueDate = this.GetTransferValueDate.bind(this);
    this.GetSwiftCountries = this.GetSwiftCountries.bind(this);
    this.GetCurrencySearch = this.GetCurrencySearch.bind(this);
    this.GetOrderCharges = this.GetOrderCharges.bind(this);
    this.ForecastRouting = this.ForecastRouting.bind(this);
  }

  Routing(
    { debitAccountNumber, creditAccountNumber, orderCurrency, orderAmount, creditAccountType, isIban }: Routing,
    nextCallback: SuccessCallback,
  ): Promise<RoutingResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        headers: {
          Accept: 'application/vnd.intrasoft.v2+json',
        },
        url: '/secure-web/api/private/transfers/routing',
        data: {
          debitAccountNumber,
          creditAccountNumber,
          orderCurrency,
          orderAmount,
          creditAccountType,
          isIban
        },
        nextCallback,
        dataAdapter: routingDTOToRoutingAdapter,
      },
    });
  }

  Forecast(data: Forecast, nextCallback: SuccessCallback): Promise<ForecastResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: '/secure-web/api/private/transfers/forecast',
        data,
        nextCallback,
        dataAdapter: forecastDTOToForecastAdapter,
      },
    });
  }

  Vop(data: Vop, nextCallback: SuccessCallback): Promise<VopResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: '/secure-web/api/private/transfers/vop',
        data,
        nextCallback,
        dataAdapter: vopDTOToVopAdapter,
      },
    });
  }

  ForecastRouting(
    debitAccount: string,
    creditAccount: string,
    bic: string,
    amount: number,
    currency: number,
    charges: number,
    chargesDescription: string,
  ): Promise<ForecastRoutingResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: '/secure-web/api/private/transfers/forecast/routing',
        data: {
          debitAccount,
          creditAccount,
          bic,
          amount,
          currency,
          charges,
          chargesDescription,
        },
        dataAdapter: forecastRoutingDTOToForecastRoutingAdapter,
      },
    });
  }

  ExchangeCalculate(data: ExchangeCalculate, nextCallback?: SuccessCallback): Promise<ExchangeCalculateResponseBody> {
    return awaitDataServiceFetcher({
      payload: Object.assign({
        method: 'POST',
        url: '/secure-web/api/private/transfers/exchange/calculate',
        data: { ...data, sideType: data.isSideTypeBuy ? 'BUY' : 'SELL' },
        nextCallback,
        dataAdapter: exchangeCalculateDTOToExchangeCalculateAdapter,
      }),
    });
  }

  ExchangeRates(
    data?: ExchangeRates,
    nextCallback?: SuccessCallback,
    companyId?: string,
  ): Promise<ExchangeRatesResponseBody> {
    return awaitDataServiceFetcher({
      payload: Object.assign({
        method: 'GET',
        url: '/secure-web/api/private/transfers/exchange/rates',
        params: data,
        nextCallback,
        dataAdapter: exchangeRatesDTOToExchangeRatesAdapter,
      }),
    });
  }

  Initiate(data: InitiateTransferRequest, nextCallback: SuccessCallback): Promise<InitiateTransferResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: '/secure-web/api/private/transfers/initiate',
        data,
        dataAdapter: initiateTransferDTOToInitiateTransferAdapter,
        nextCallback,
      },
    });
  }

  GetDocuments(data: { transactionId: string }): Promise<Document[]> {
    const { transactionId } = data;
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/api/private/transfers/documents/${transactionId}`,
        dataAdapter: documentsResponseDTOToDocumentsAdapter,
      },
    });
  }

  PostDocument(
    data: PostDocument & { companyId?: string | undefined; nextCallback?: SuccessCallback },
    nextCallback?: SuccessCallback | undefined,
  ): Promise<PostDocumentResponseBody> {
    const { transactionId, fileName, fileDescription, fileData } = data;
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: '/secure-web/api/private/transfers/documents',
        data: {
          transactionId,
          fileName,
          fileDescription,
          fileData,
        },
        dataAdapter: postDocumentResponseDTOToPostDocumentAdapter,
        nextCallback: data?.nextCallback || nextCallback,
      },
    });
  }

  DeleteDocument(
    data: DeleteDocument & { nextCallback?: SuccessCallback },
    nextCallback?: SuccessCallback | undefined,
  ): Promise<any> {
    const { transactionId, fileName, fileDescription, fileData, fileId } = data;
    return awaitDataServiceFetcher({
      payload: {
        method: 'DELETE',
        url: '/secure-web/api/private/transfers/documents',
        data: {
          transactionId,
          fileName,
          fileDescription,
          fileData,
          fileId,
        },
        dataAdapter: normalizeEmptyResponseToSuccessResponse,
        nextCallback: data?.nextCallback || nextCallback,
      },
    });
  }
  //TODO fix make transfer request object and response
  MakeTransfer(data: { requestId: string }): Promise<MakeTransferResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'PUT',
        url: `/secure-web/api/private/transfers/transfer/${data.requestId}`,
        dataAdapter: makeTransferDTOToMakeTransferAdapter,
      },
    });
  }

  GetBankSwiftCodes(data: { cityName: string; countryName: string; bankName: string }): Promise<SwiftCode[]> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/api/private/transfers/swift/codes`,
        params: {
          cityName: data.cityName,
          bankName: data.bankName,
          countryName: data.countryName,
        },
        dataAdapter: swiftCodesResponseDTOToSwiftCodesAdapter,
      },
    });
  }

  GetSwiftCountries(): Promise<SwiftCountry[]> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/api/private/transfers/swift/countries`,
        dataAdapter: swiftCountriesResponseDTOToSwiftCountriesResponseAdapter,
      },
    });
  }

  GetSwiftCities(data: { countryName: string }): Promise<SwiftCity[]> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/api/private/transfers/swift/cities`,
        params: { countryName: data.countryName },
        dataAdapter: swiftCitiesResponseDTOToSwiftCitiesResponseAdapter,
      },
    });
  }

  GetSwiftBanks(data: { countryName: string; cityName: string }): Promise<SwiftBank[]> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/api/private/transfers/swift/banks`,
        params: { countryName: data.countryName, cityName: data.cityName },
        dataAdapter: swiftBanksResponseDTOToSwiftBanksAdapter,
      },
    });
  }

  GetBopCodes(bopCode: BopCodesIdentifiers): Promise<BopCode[]> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/api/private/transfers/parameters/bop-codes`,
        params: { bopCode },
        dataAdapter: bopCodesResponseDTOToBopCodesAdapter,
      },
    });
  }

  GetVocCodes(): Promise<VocCode[]> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/api/private/transfers/parameters/voc-codes`,
        dataAdapter: vocCodesResponseDTOToVocCodesAdapter,
      },
    });
  }

  DealerRateExecute(
    data: DealerRateExecute,
    nextCallback?: SuccessCallback | undefined,
  ): Promise<DealerRateExecuteResponseBody> {
    const { requestId, dealerRateRefNumber, dealerUserCode, dealerRate } = data;
    return awaitDataServiceFetcher({
      payload: Object.assign({
        method: 'PUT',
        url: '/secure-web/api/private/transfers/dealer-rate/execute',
        data: {
          requestId,
          dealerRateRefNumber,
          dealerUserCode,
          dealerRate,
        },
        nextCallback,
        dataAdapter: dealerRateExecuteDTOToDealerRateExecuteAdapter,
      }),
    });
  }

  GetDealerRates(referenceNumber: string, dealerUserCode: string): Promise<DealerRatesWithCalculatedTimer> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: '/secure-web/api/private/transfers/exchange/dealer-rates',
        params: {
          referenceNumber,
          dealerUserCode,
        },
        dataAdapter: dealerRatesResponseDTOToDealerRatesAdapter,
      },
    });
  }

  InitiateRequestDealerTransfer(
    orderCode: string,
    appTransactionId: number,
    nextCallback?: SuccessCallback,
  ): Promise<InitiateTransferResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: '/secure-web/api/private/transfers/dealer-rate/initiate',
        data: {
          orderCode,
          appTransactionId,
        },
        nextCallback,
        dataAdapter: initiateTransferDTOToInitiateTransferAdapter,
      },
    });
  }

  GetSwiftCodesByBic(data: { bic: string; nextCallback?: SuccessCallback | undefined }): Promise<SwiftCode> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: `/secure-web/api/private/transfers/swift/bic/validate`,
        data: { bicCode: data.bic },
        nextCallback: data.nextCallback,
        dataAdapter: bicSwiftCodesResponseDTOToSwiftCodesAdapter,
      },
    });
  }

  RequestNewDealerRate(orderCode: string, nextCallback?: SuccessCallback | undefined): Promise<{}> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: `/api/private/transfers/dealer-rate/new`,
        data: { orderCode },
        nextCallback,
        dataAdapter: normalizeEmptyResponseToSuccessResponse,
      },
    });
  }

  RejectDealerRate(orderCode: string, nextCallback?: SuccessCallback | undefined): Promise<{}> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: `/api/private/transfers/dealer-rate/cancel`,
        data: { orderCode },
        nextCallback,
        dataAdapter: normalizeEmptyResponseToSuccessResponse,
      },
    });
  }

  InitiateCurrentDealerRate(
    orderCode: string,
    appTransactionId: number,
    nextCallback?: SuccessCallback | undefined,
  ): Promise<InitiateTransferResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: `/api/private/transfers/current-rate/initiate`,
        data: { orderCode, appTransactionId },
        nextCallback,
        dataAdapter: initiateTransferDTOToInitiateTransferAdapter,
      },
    });
  }

  ExecuteWithCurrentDealerRate(
    requestId: string,
    nextCallback?: SuccessCallback | undefined,
  ): Promise<DealerRateExecuteResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'PUT',
        url: `/api/private/transfers/current-rate/execute/${requestId}`,
        nextCallback,
        dataAdapter: dealerRateExecuteDTOToDealerRateExecuteAdapter,
      },
    });
  }

  GetTransferValueDate(
    priorityDate: Priorities,
    nextCallback?: SuccessCallback,
    onErrorCallback?: ErrorCallback,
  ): Promise<ValueDateResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/api/private/transfers/date`,
        params: { priorityDate },
        nextCallback,
        onErrorCallback,
        dataAdapter: valueDateResponseDTOToValueDateResponseAdapter,
      },
    });
  }

  GetCurrencySearch(data: CurrencySearch): Promise<CurrencySearchResponseBody> {
    const { isIban, accountIdentifier } = data;
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/api/private/accounts/currencies/search`,
        params: { isIban, accountIdentifier },
        dataAdapter: currencySearchResponseDTOToCurrencySearchResponseAdapter,
      },
    });
  }

  GetOrderCharges(orderCode: string): Promise<OrderChargesResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/api/private/transfers/${orderCode}/charges`,
        dataAdapter: orderChargesResponseDTOToorderChargesResponseAdapter,
      },
    });
  }
}

export { TransferRepositoryImpl };
